import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth-provider/context';

// Define allowed routes for each role
const ROLE_ROUTES = {
  finance: [
    '/payment/dashboard',
    '/payment/school-fee',
    '/payment/cash',
    '/payment/status-pembayaran',
    '/payment/pencatatan',
    '/payment/laporan',
  ],
  operator: [
    // Operators have access to all routes except restricted ones
    '/',
  ],
  superoperator: [
    // Superoperators have access to all routes (based on menu structure)
    '/',
  ],
};

// Define restricted routes for specific roles
const RESTRICTED_ROUTES = {
  operator: ['/school/operator'],
};

// Default redirect routes for each role
const DEFAULT_REDIRECTS = {
  finance: '/payment/dashboard',
  operator: '/dashboard',
  superoperator: '/dashboard',
};

/**
 * Middleware component to handle role-based access control
 * @param {Object} children - Child components to render if access is allowed
 */
function RoleMiddleware({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Don't apply middleware if user is not authenticated or no role
    if (!user || !user.role) {
      return;
    }

    const currentPath = location.pathname;
    const userRole = user.role;

    // Get allowed routes for the user's role
    const allowedRoutes = ROLE_ROUTES[userRole] || [];
    const restrictedRoutes = RESTRICTED_ROUTES[userRole] || [];

    // If role has access to all routes (indicated by '/'), check for restricted routes
    if (allowedRoutes.includes('/')) {
      // Check if current path is in restricted routes
      const isRestricted = restrictedRoutes.some((route) => {
        // For exact matches
        if (route === currentPath) {
          return true;
        }
        // For route patterns (e.g., /school/* should match /school/operator)
        if (route.endsWith('/*')) {
          const baseRoute = route.slice(0, -2);
          return currentPath.startsWith(baseRoute);
        }
        // For base routes (e.g., /school should match /school/operator)
        return currentPath.startsWith(route + '/') || currentPath === route;
      });

      // If access is restricted, redirect to the default route for the role
      if (isRestricted) {
        const redirectRoute = DEFAULT_REDIRECTS[userRole] || '/dashboard';
        navigate(redirectRoute, { replace: true });
      }
      return;
    }

    // Check if current path is allowed for the user's role
    const isAllowed = allowedRoutes.some((route) => {
      // For exact matches
      if (route === currentPath) {
        return true;
      }
      // For route patterns (e.g., /payment/* should match /payment/dashboard)
      if (route.endsWith('/*')) {
        const baseRoute = route.slice(0, -2);
        return currentPath.startsWith(baseRoute);
      }
      // For base routes (e.g., /payment should match /payment/dashboard)
      return currentPath.startsWith(route + '/') || currentPath === route;
    });

    // If access is not allowed, redirect to the default route for the role
    if (!isAllowed) {
      const redirectRoute = DEFAULT_REDIRECTS[userRole] || '/dashboard';
      navigate(redirectRoute, { replace: true });
    }
  }, [location.pathname, user, navigate]);

  return children;
}

export default RoleMiddleware;
